import { useStore } from 'dg-web-shared/lib/Flux.tsx';
import * as CurrentOperatorLoginState from '../../common/state/CurrentOperatorLoginState.ts';
import { Localized } from 'dg-web-shared/lib/Localization.tsx';
import { MainContent } from '../../shared-mui-components/MainContent.tsx';
import { HeaderWithButtons } from '../../shared-mui-components/HeaderWithButtons.tsx';
import { LocalParking } from '@mui/icons-material';
import { Box } from '@mui/material';
import { Outlet, useNavigate, useOutletContext } from 'react-router-dom';
import {
    LastUpdateTimestampAndRefresh,
    LastUpdateTimestampAndRefreshAsyncLoadedSection,
} from '../../ui/LastUpdateTimestampAndRefresh.tsx';
import { OperatorAsyncLoadedSection } from '../../app/components/OperatorAsyncLoadedSection.tsx';
import { LoadingSpinnerPresets } from 'dg-web-shared/common/components/material-ui/PresetLoadingSpinner.tsx';
import { EmptyResults } from '../../shared-mui-components/ListMessages.tsx';
import {
    ServerRequestState,
    useServerFetch,
} from 'dg-web-shared/lib/hooks/ServerStateHooks.ts';
import { ToggleGroup } from '../../shared-mui-components/ToggleGroup.tsx';
import { ZonesMap } from './ZonesMap.tsx';
import { ZonesList } from './ZonesList.tsx';
import { LicensePlateType } from 'dg-web-shared/dto/LicensePlateType.ts';

export const enum ZonesTab {
    MAP = 'MAP',
    LIST = 'LIST',
}
export interface ZoneEnforcedDTO {
    zoneId: number;
    zipCode: string;
    licensePlateTypes: LicensePlateType[];
    city: string;
    zoneName: string;
    zoneCode: number;
    needsPermit: boolean;
    vatNumber: string | null;
    isFromForeignMandant: boolean;
    hideInEnforcement: boolean;
    geodataText: string | null;
    relevance: ZoneEnforcedZoneRelevance;
    signalisationPdfOnstreet: string | null;
    dtaAccountDescription: string | null;
    showAccount: boolean;
}

export interface OnstreetZoneDetailDTO extends ZoneEnforcedDTO {
    tariffsValidFrom: string | null;
    tariffs: Tariffs[];
    permitTypes: PermitType[];
}
export type PermitType = {
    permitId: number;
    permitName: string;
};
export const enum TariffContextType {
    TOM = 'TOM',
    PARKINGPAY = 'PARKINGPAY',
}
export type Tariffs = {
    tariffId: number;
    tariffName: string;
    operatorId: number;
    contextType: TariffContextType;
};

type ZoneEnforcedZoneRelevance =
    | ZoneEnforcedZoneRelevanceActive
    | ZoneEnforcedZoneRelevanceHasNonActiveUsages;

type ZoneEnforcedZoneRelevanceActive = {
    tag: ZoneEnforcedZoneRelevanceTag.ACTIVE;
};

type ZoneEnforcedZoneRelevanceHasNonActiveUsages = {
    tag: ZoneEnforcedZoneRelevanceTag.HAS_NON_ACTIVE_USAGES;
    hasCheckIns: boolean;
    hasEnforcementLogEntries: boolean;
};

export enum ZoneEnforcedZoneRelevanceTag {
    ACTIVE = 'ACTIVE',
    HAS_NON_ACTIVE_USAGES = 'HAS_NON_ACTIVE_USAGES',
}

export function Zones({ tab }: { tab: ZonesTab }) {
    const navigate = useNavigate();

    const [zonesState, refetchZones] = useServerFetch<
        ZoneEnforcedDTO[],
        object
    >(
        () => ({
            url: `/ui-api/operator-account/onstreet-zones`,
        }),
        {},
    );

    const { storeState } = useStore(store => ({
        currentLogin: CurrentOperatorLoginState.get(store),
    }));

    if (!storeState.currentLogin.data) {
        return null;
    }

    return (
        <>
            <MainContent>
                <HeaderWithButtons
                    title={
                        <Localized de="Zones" fr="Zones" it="Zone" en="Zones" />
                    }
                    icon={LocalParking}
                />
                <Box
                    sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        paddingX: 3,
                        height: 55,
                        py: 1,
                        backgroundColor: 'blue.light',
                    }}
                >
                    <Box
                        sx={{
                            flexGrow: 1,
                            maxWidth: '33%',
                        }}
                    >
                        <ToggleGroup
                            value={tab}
                            onChange={d => {
                                switch (d) {
                                    case ZonesTab.MAP:
                                        navigate('..');
                                        break;
                                    case ZonesTab.LIST:
                                        navigate('list');
                                        break;
                                }
                            }}
                            valueRenders={[
                                {
                                    value: ZonesTab.LIST,
                                    description: (
                                        <Localized
                                            de="Zonenliste"
                                            fr="Liste des zones"
                                            it="Lista zone"
                                            en="Zone list"
                                        />
                                    ),
                                },
                                {
                                    value: ZonesTab.MAP,
                                    description: (
                                        <Localized
                                            de="Karte"
                                            fr="Plan"
                                            it="Mappa"
                                            en="Map"
                                        />
                                    ),
                                },
                            ]}
                        />
                    </Box>
                    <LastUpdateTimestampAndRefreshAsyncLoadedSection
                        requestState={zonesState}
                        onRefresh={refetchZones}
                        render={(list, timestamp) => (
                            <LastUpdateTimestampAndRefresh
                                resultsCount={list.filter(isActiveZone).length}
                                eof={true}
                                onRefresh={refetchZones}
                                timestamp={timestamp}
                            />
                        )}
                    />
                </Box>
                <OperatorAsyncLoadedSection
                    requestState={zonesState}
                    pendingLoaderPreset={
                        LoadingSpinnerPresets.FillAllSpaceAndCenter
                    }
                    render={zones => {
                        const activeZones = zones.filter(isActiveZone);

                        if (activeZones.length < 1) {
                            return <EmptyResults />;
                        }

                        switch (tab) {
                            case ZonesTab.MAP:
                                return <ZonesMap zones={activeZones} />;
                            case ZonesTab.LIST:
                                return <ZonesList zones={activeZones} />;
                        }
                    }}
                />
            </MainContent>
            <ZonesOutlet zonesState={zonesState} refetchZones={refetchZones} />
        </>
    );
}

function ZonesOutlet({ zonesState, refetchZones }: ZonesOutletContext) {
    return <Outlet context={{ zonesState, refetchZones }} />;
}

type ZonesOutletContext = {
    zonesState: ServerRequestState<ZoneEnforcedDTO[], unknown>;
    refetchZones: () => void;
};
export function useZonesOutletContext() {
    return useOutletContext<ZonesOutletContext>();
}

function isActiveZone(zone: ZoneEnforcedDTO) {
    return (
        !zone.isFromForeignMandant &&
        zone.relevance.tag === ZoneEnforcedZoneRelevanceTag.ACTIVE
    );
}
